/** Date helpers. All day boundaries are local time. */

export const MS_DAY = 86_400_000;

export function startOfDay(d: Date | number): Date {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  return date;
}

export function endOfDay(d: Date | number): Date {
  const date = new Date(d);
  date.setHours(23, 59, 59, 999);
  return date;
}

export function addDays(d: Date | number, days: number): Date {
  const date = new Date(d);
  date.setDate(date.getDate() + days);
  return date;
}

export function addMonths(d: Date | number, months: number): Date {
  const date = new Date(d);
  const day = date.getDate();
  date.setDate(1);
  date.setMonth(date.getMonth() + months);
  date.setDate(Math.min(day, daysInMonth(date.getFullYear(), date.getMonth())));
  return date;
}

export function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/**
 * Monday = 1 ... Sunday = 7 (ISO).
 * `firstDayOfWeek` uses the same convention.
 */
export function startOfWeek(d: Date | number, firstDayOfWeek = 1): Date {
  const date = startOfDay(d);
  const currentIso = date.getDay() === 0 ? 7 : date.getDay();
  const diff = (currentIso - firstDayOfWeek + 7) % 7;
  date.setDate(date.getDate() - diff);
  return date;
}

export function startOfMonth(d: Date | number): Date {
  const date = startOfDay(d);
  date.setDate(1);
  return date;
}

export function endOfMonth(d: Date | number): Date {
  const date = startOfMonth(d);
  date.setMonth(date.getMonth() + 1);
  date.setDate(0);
  return date;
}

export function isSameDay(a: Date | number, b: Date | number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

export function isToday(d: Date | number): boolean {
  return isSameDay(d, new Date());
}

/** yyyy-MM-dd in local time. */
export function dayKey(d: Date | number): string {
  const date = new Date(d);
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${m}-${day}`;
}

export function fromDayKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

const WEEKDAYS_VI = [
  "Chủ nhật",
  "Thứ hai",
  "Thứ ba",
  "Thứ tư",
  "Thứ năm",
  "Thứ sáu",
  "Thứ bảy",
];

const WEEKDAYS_SHORT_VI = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
/** Indexed by ISO weekday number (1 = Monday ... 7 = Sunday). */
export const WEEKDAY_LABELS_ISO = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];
const MONTHS_VI = [
  "Tháng 1",
  "Tháng 2",
  "Tháng 3",
  "Tháng 4",
  "Tháng 5",
  "Tháng 6",
  "Tháng 7",
  "Tháng 8",
  "Tháng 9",
  "Tháng 10",
  "Tháng 11",
  "Tháng 12",
];

export const weekdayName = (d: Date | number) => WEEKDAYS_VI[new Date(d).getDay()];
export const weekdayShort = (d: Date | number) =>
  WEEKDAYS_SHORT_VI[new Date(d).getDay()];
export const monthName = (d: Date | number) => MONTHS_VI[new Date(d).getMonth()];

/** "12 tháng 3, 2026" */
export function formatDate(d: Date | number): string {
  const date = new Date(d);
  return `${date.getDate()} tháng ${date.getMonth() + 1}, ${date.getFullYear()}`;
}

export function formatTime(d: Date | number, use24h = true): string {
  const date = new Date(d);
  const h = date.getHours();
  const m = `${date.getMinutes()}`.padStart(2, "0");
  if (use24h) return `${`${h}`.padStart(2, "0")}:${m}`;
  const suffix = h < 12 ? "SA" : "CH";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${m} ${suffix}`;
}

/** "Hôm nay", "Ngày mai", "Hôm qua" or the weekday name. */
export function relativeDayLabel(d: Date | number): string {
  const today = startOfDay(new Date()).getTime();
  const target = startOfDay(d).getTime();
  const diff = Math.round((target - today) / MS_DAY);
  if (diff === 0) return "Hôm nay";
  if (diff === 1) return "Ngày mai";
  if (diff === -1) return "Hôm qua";
  if (diff > 1 && diff <= 6) return weekdayName(d);
  return formatDate(d);
}

/** Human readable difference, e.g. "còn 3 ngày", "quá hạn 2 giờ". */
export function dueLabel(due: number | null | undefined, now = Date.now()): string | null {
  if (!due) return null;
  const diff = due - now;
  const abs = Math.abs(diff);
  const minutes = Math.round(abs / 60_000);
  const hours = Math.round(abs / 3_600_000);
  const days = Math.round(abs / MS_DAY);

  let value: string;
  if (minutes < 60) value = `${minutes} phút`;
  else if (hours < 24) value = `${hours} giờ`;
  else value = `${days} ngày`;

  return diff >= 0 ? `còn ${value}` : `quá hạn ${value}`;
}

/** 6x7 grid covering the month, starting on the configured first weekday. */
export function monthMatrix(
  anchor: Date,
  firstDayOfWeek = 1,
): { date: Date; inMonth: boolean }[] {
  const first = startOfMonth(anchor);
  const gridStart = startOfWeek(first, firstDayOfWeek);
  const cells: { date: Date; inMonth: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const date = addDays(gridStart, i);
    cells.push({ date, inMonth: date.getMonth() === anchor.getMonth() });
  }
  return cells;
}
